import React from "react";

import { faBoxArchive, faCheck } from "@fortawesome/pro-regular-svg-icons";

import { FlowVersion } from "src/api/flowTypes";
import { Pill } from "src/base-components/Pill";
import {
  etag_including_comments,
  useLoadChangeHistory,
} from "src/changeHistory/queries";
import { FlowVersionStatus } from "src/clients/flow-api";
import { UpdatedByInfo } from "src/flowContainer/versionEditedInfo/utils";
import { useRelativeTime } from "src/hooks/useRelativeTime";
import { assertUnreachable } from "src/utils/typeUtils";

type Props = { flowVersion: FlowVersion };

const getStatusPrefix = (status: FlowVersionStatus) => {
  switch (status) {
    case FlowVersionStatus.DRAFT:
      return "Updated";
    case FlowVersionStatus.PUBLISHED:
      return "Published";
    case FlowVersionStatus.ARCHIVED:
      return "Archived";
    default:
      assertUnreachable(status);
  }
};

const getPillColor = (status: FlowVersionStatus) => {
  switch (status) {
    case FlowVersionStatus.DRAFT:
      return "indigo-light";
    case FlowVersionStatus.PUBLISHED:
      return "green";
    case FlowVersionStatus.ARCHIVED:
      return "gray";
    default:
      assertUnreachable(status);
  }
};

export const LastUpdatedInfo: React.FC<Props> = ({ flowVersion }) => {
  const changeHistory = useLoadChangeHistory(
    flowVersion.id,
    etag_including_comments(flowVersion.etag),
  );
  const latestChange = changeHistory.data?.pages[0][0];
  const timeFromNow = useRelativeTime(latestChange?.updated_at);

  return (
    <Pill
      fontType="text"
      dataLoc="last-updated-info"
      variant={getPillColor(flowVersion.status)}
    >
      <Pill.Icon
        icon={
          flowVersion.status === FlowVersionStatus.ARCHIVED
            ? faBoxArchive
            : faCheck
        }
      />
      <Pill.Text>
        {getStatusPrefix(flowVersion.status)} {timeFromNow}{" "}
        <UpdatedByInfo updatedBy={latestChange?.authored_by} />
      </Pill.Text>
    </Pill>
  );
};
